<div class="support-panel">

  @switch (view()) {

    <!-- ------------------------------------------------------------------ -->
    <!-- List of the user's tickets                                          -->
    <!-- ------------------------------------------------------------------ -->
    @case ('list') {
      <div class="support-panel__body">
        @if (!hasThreads()) {
          <div class="support-panel__empty">
            <mat-icon class="support-panel__empty-icon">support_agent</mat-icon>
            <p class="support-panel__empty-title">{{ 'support.empty.title' | translate }}</p>
            <p class="support-panel__empty-text">{{ 'support.empty.text' | translate }}</p>
          </div>
        } @else {
          <ul class="support-panel__list" role="list">
            @for (thread of threads(); track thread.id) {
              <li>
                <button
                  type="button"
                  class="support-panel__item"
                  [class.support-panel__item--unread]="thread.unreadForUser"
                  (click)="openThread(thread)">
                  <span class="support-panel__item-head">
                    @if (thread.unreadForUser) {
                      <span class="support-panel__dot" aria-hidden="true"></span>
                    }
                    <span class="support-panel__item-subject">{{ thread.subject }}</span>
                  </span>
                  <span class="support-panel__item-meta">
                    <span class="support-panel__badge support-panel__badge--{{ thread.status }}">
                      {{ statusLabel(thread) | translate }}
                    </span>
                    @if (thread.lastMessageAt) {
                      <span>{{ thread.lastMessageAt | date: 'short' }}</span>
                    }
                  </span>
                </button>
              </li>
            }
          </ul>
        }
      </div>

      <footer class="support-panel__footer">
        <button type="button" mat-flat-button color="primary" (click)="startCompose()">
          <mat-icon>add</mat-icon>
          {{ 'support.actions.newTicket' | translate }}
        </button>
      </footer>
    }

    <!-- ------------------------------------------------------------------ -->
    <!-- New ticket                                                          -->
    <!-- ------------------------------------------------------------------ -->
    @case ('compose') {
      <div class="support-panel__body">
        <button type="button" class="support-panel__back" (click)="backToList()">
          <mat-icon>arrow_back</mat-icon>
          {{ 'support.actions.back' | translate }}
        </button>

        <!--
          Who is being written to (#292). Only a tenant has two possible
          correspondents; for everyone else the picker would be a question with
          one answer.
        -->
        @if (canChooseAudience()) {
          <span class="support-panel__label">{{ 'support.form.recipient' | translate }}</span>
          <div class="support-panel__audience" role="radiogroup">
            <button
              type="button"
              role="radio"
              class="support-panel__audience-option"
              [class.support-panel__audience-option--active]="audience() === 'workspace'"
              [attr.aria-checked]="audience() === 'workspace'"
              (click)="audience.set('workspace')">
              <mat-icon>apartment</mat-icon>
              <span>{{ 'support.form.recipientManager' | translate }}</span>
            </button>
            <button
              type="button"
              role="radio"
              class="support-panel__audience-option"
              [class.support-panel__audience-option--active]="audience() === 'proporio'"
              [attr.aria-checked]="audience() === 'proporio'"
              (click)="audience.set('proporio')">
              <mat-icon>support_agent</mat-icon>
              <span>{{ 'support.form.recipientProporio' | translate }}</span>
            </button>
          </div>
        }

        <!--
          Says what is riding along, before the send. The transcript shares the
          consent checkbox below: unticking it drops both the technical context
          and the conversation.
        -->
        @if (handoffDraft()) {
          <p class="support-panel__handoff-note">
            <mat-icon class="support-panel__handoff-icon">forum</mat-icon>
            {{ 'support.handoff.attached' | translate }}
          </p>
        }

        <label class="support-panel__label" for="support-subject">
          {{ 'support.form.subject' | translate }}
        </label>
        <input
          id="support-subject"
          class="support-panel__input"
          type="text"
          [maxlength]="limits.subjectMaxLength"
          [placeholder]="'support.form.subjectPlaceholder' | translate"
          [ngModel]="newSubject()"
          (ngModelChange)="newSubject.set($event)" />

        <label class="support-panel__label" for="support-message">
          {{ 'support.form.message' | translate }}
        </label>
        <textarea
          id="support-message"
          class="support-panel__textarea"
          rows="6"
          [maxlength]="limits.messageMaxLength"
          [placeholder]="'support.form.messagePlaceholder' | translate"
          [ngModel]="newMessage()"
          (ngModelChange)="newMessage.set($event)"></textarea>

        <!--
          Consent, ticked by default, with the payload one click away. The
          disclosure is the point: agreeing to "technical information" means
          nothing if you cannot see that it includes your last errors.
        -->
        <div class="support-panel__consent">
          <label class="support-panel__checkbox">
            <input
              type="checkbox"
              [ngModel]="attachContext()"
              (ngModelChange)="attachContext.set($event)" />
            <span>{{ 'support.form.attachContext' | translate }}</span>
          </label>
          <button type="button" class="support-panel__link" (click)="toggleContextPreview()">
            {{ (contextPreviewOpen() ? 'support.form.hideDetail' : 'support.form.showDetail') | translate }}
          </button>
        </div>
        @if (contextPreviewOpen()) {
          <pre class="support-panel__context">{{ contextPreview() }}</pre>
        }
      </div>

      <footer class="support-panel__footer">
        <!--
          The tooltip sits on the WRAPPER: a disabled button fires no pointer
          event, so a tooltip bound to it would never show. DESIGN.md §13.8 —
          a control disabled by a business condition must say what lifts it.
        -->
        <span
          [matTooltip]="canSubmitNew() ? '' : ('support.hints.fillSubjectAndMessage' | translate)"
          [matTooltipDisabled]="canSubmitNew()">
          <button
            type="button"
            mat-flat-button
            color="primary"
            [disabled]="!canSubmitNew()"
            (click)="submitNew()">
            @if (sending()) {
              <mat-spinner diameter="18"></mat-spinner>
            } @else {
              {{ 'support.actions.send' | translate }}
            }
          </button>
        </span>
      </footer>
    }

    <!-- ------------------------------------------------------------------ -->
    <!-- One conversation                                                    -->
    <!-- ------------------------------------------------------------------ -->
    @case ('thread') {
      <div class="support-panel__thread-head">
        <button type="button" class="support-panel__back" (click)="backToList()">
          <mat-icon>arrow_back</mat-icon>
        </button>
        <span class="support-panel__thread-subject">{{ activeThread()?.subject }}</span>
        @if (activeThread(); as thread) {
          <span class="support-panel__badge support-panel__badge--{{ thread.status }}">
            {{ statusLabel(thread) | translate }}
          </span>
          @if (thread.status !== 'closed') {
            <button
              type="button"
              mat-icon-button
              [matTooltip]="'support.actions.close' | translate"
              (click)="closeThread()">
              <mat-icon>check_circle</mat-icon>
            </button>
          }
        }
      </div>

      <div class="support-panel__body support-panel__body--messages">
        @if (loadingMessages()) {
          <div class="support-panel__loading">
            <mat-spinner diameter="24"></mat-spinner>
          </div>
        }
        @for (message of messages(); track message.id) {
          @if (message.role !== 'system') {
            <article
              class="support-panel__message"
              [class.support-panel__message--staff]="message.role === 'staff'">
              <header class="support-panel__message-head">
                <span class="support-panel__message-author">
                  {{ message.role === 'staff'
                      ? ('support.staffName' | translate)
                      : message.authorName }}
                </span>
                @if (message.createdAt) {
                  <span class="support-panel__message-date">
                    {{ message.createdAt | date: 'short' }}
                  </span>
                }
              </header>
              <p class="support-panel__message-body">{{ displayedContent(message) }}</p>
              <span
                [matTooltip]="'support.hints.translating' | translate"
                [matTooltipDisabled]="translatingId() !== message.id">
                <button
                  type="button"
                  class="support-panel__link"
                  [disabled]="translatingId() === message.id"
                  (click)="translateMessage(message)">
                  @if (translatingId() === message.id) {
                    {{ 'support.actions.translating' | translate }}
                  } @else {
                    {{ (isTranslated(message) ? 'support.actions.showOriginal' : 'support.actions.translate')
                        | translate }}
                  }
                </button>
              </span>
            </article>
          }
        }
      </div>

      <footer class="support-panel__footer support-panel__footer--composer">
        <textarea
          class="support-panel__textarea support-panel__textarea--reply"
          rows="2"
          [maxlength]="limits.messageMaxLength"
          [placeholder]="'support.form.replyPlaceholder' | translate"
          [ngModel]="reply()"
          (ngModelChange)="reply.set($event)"></textarea>
        <span
          [matTooltip]="'support.hints.writeReply' | translate"
          [matTooltipDisabled]="canReply()">
          <button
            type="button"
            mat-icon-button
            color="primary"
            [disabled]="!canReply()"
            [attr.aria-label]="'support.actions.send' | translate"
            (click)="submitReply()">
            @if (sending()) {
              <mat-spinner diameter="20"></mat-spinner>
            } @else {
              <mat-icon>send</mat-icon>
            }
          </button>
        </span>
      </footer>
    }
  }
</div>
